import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  UsersRound, Plus, Save, Trash2, Search, ChevronRight, Users2, Info,
} from "lucide-react";
import { toast } from "sonner";
import { invalidatePermissionsCache } from "@/hooks/usePermissions";

/**
 * Painel de Grupos Customizados de Permissão.
 * Renderizado dentro de PermissionsMatrixPage (aba Grupos).
 */
export default function PermissionGroupsPanel({ catalog }) {
  const { user } = useAuth();
  const canManage = user?.role === "master" || user?.role === "rh_admin";

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);      // grupo aberto no editor
  const [editing, setEditing] = useState(null);    // { id?, name, description, permissions }
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigned, setAssigned] = useState(new Set());
  const [userQuery, setUserQuery] = useState("");

  const loadGroups = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/permissions/groups");
      setGroups(data.groups || []);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao carregar grupos");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadGroups(); }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(s) || (g.description || "").toLowerCase().includes(s));
  }, [groups, search]);

  const openNew = () => {
    setEditing({ name: "", description: "", permissions: {} });
    setDetail(null);
  };

  const openEdit = async (g) => {
    // Carrega detalhes completos + users
    try {
      const { data } = await api.get(`/permissions/groups/${g.id}`);
      setDetail(data);
      setEditing({
        id: data.group.id,
        name: data.group.name,
        description: data.group.description || "",
        permissions: data.group.permissions || {},
      });
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const toggleCell = (mkey, akey) => {
    setEditing((prev) => {
      const next = { ...prev, permissions: { ...(prev.permissions || {}) } };
      next.permissions[mkey] = { ...(next.permissions[mkey] || {}) };
      next.permissions[mkey][akey] = !next.permissions[mkey][akey];
      return next;
    });
  };

  const bulkRow = (mkey, val) => {
    setEditing((prev) => {
      const next = { ...prev, permissions: { ...(prev.permissions || {}) } };
      next.permissions[mkey] = {};
      catalog.actions.forEach((a) => { next.permissions[mkey][a.key] = val; });
      return next;
    });
  };

  const save = async () => {
    if (!editing?.name?.trim()) { toast.error("Informe o nome do grupo"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description || "",
        permissions: editing.permissions || {},
      };
      let saved;
      if (editing.id) {
        const { data } = await api.put(`/permissions/groups/${editing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/permissions/groups", payload);
        saved = data;
      }
      invalidatePermissionsCache();
      toast.success(`Grupo "${saved.name}" salvo`);
      await loadGroups();
      // Mantém aberto no modo edição
      await openEdit(saved);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/permissions/groups/${confirmDelete.id}`);
      invalidatePermissionsCache();
      toast.success(`Grupo "${confirmDelete.name}" excluído`);
      setConfirmDelete(null);
      setEditing(null); setDetail(null);
      await loadGroups();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const openAssign = async () => {
    if (!editing?.id) { toast.error("Salve o grupo antes de vincular usuários"); return; }
    try {
      const { data } = await api.get("/users");
      setUsers(Array.isArray(data) ? data : data.users || []);
      // Marca os que já estão vinculados
      const curr = new Set((detail?.users || []).map((u) => u.id));
      setAssigned(curr);
      setAssignOpen(true);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const toggleUser = (uid) => {
    setAssigned((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const saveAssign = async () => {
    try {
      const { data } = await api.post(`/permissions/groups/${editing.id}/assign`, {
        user_ids: Array.from(assigned),
      });
      invalidatePermissionsCache();
      toast.success(`${data.user_count} usuário(s) vinculado(s)`);
      setAssignOpen(false);
      await openEdit({ id: editing.id });
      await loadGroups();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const usersFiltered = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || (u.role || "").toLowerCase().includes(q)
    );
  }, [users, userQuery]);

  return (
    <div className="space-y-4" data-testid="permission-groups-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative w-full max-w-[320px]">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar grupo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="groups-search"
            />
          </div>
          <Badge variant="outline">{filtered.length} grupo(s)</Badge>
        </div>
        {canManage && (
          <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="groups-new-btn">
            <Plus className="h-4 w-4 mr-1" /> Novo grupo
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Lista */}
        <div className="rounded-xl border border-border bg-white overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="groups-list">
          {loading && <div className="p-4 text-muted-foreground text-[13px]">Carregando...</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-6 text-center text-[13px] text-muted-foreground">
              <UsersRound className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Nenhum grupo cadastrado.
              {canManage && <div className="mt-2">Crie o primeiro clicando em <b>Novo grupo</b>.</div>}
            </div>
          )}
          {filtered.map((g) => {
            const isActive = editing?.id === g.id;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => openEdit(g)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50/60 flex items-center gap-3 ${isActive ? "bg-[#F1E9CF]/60" : ""}`}
                data-testid={`group-item-${g.id}`}
              >
                <div className="h-9 w-9 rounded-lg bg-[#234935]/10 grid place-items-center shrink-0">
                  <UsersRound className="h-4 w-4 text-[#234935]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[13.5px] truncate">{g.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{g.description || "sem descrição"}</div>
                </div>
                <Badge variant="outline" className="shrink-0">{g.user_count}</Badge>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            );
          })}
        </div>

        {/* Editor */}
        <div className="rounded-xl border border-border bg-white">
          {!editing && (
            <div className="p-8 text-center text-muted-foreground text-[13px]" data-testid="groups-empty-editor">
              <Info className="h-8 w-8 mx-auto mb-2 opacity-40" />
              Selecione um grupo à esquerda ou crie um novo para editar permissões.
            </div>
          )}
          {editing && (
            <div className="p-5 space-y-4" data-testid="groups-editor">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Nome do grupo</label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Ex: Auditores Externos" disabled={!canManage}
                    data-testid="groups-name-input"
                  />
                </div>
                {editing.id && (
                  <Badge variant="outline" className="mt-6">
                    <Users2 className="h-3.5 w-3.5 mr-1" />
                    {detail?.users?.length || 0} usuário(s)
                  </Badge>
                )}
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Descrição</label>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={2} placeholder="Como este grupo será utilizado?"
                  disabled={!canManage} data-testid="groups-description-input"
                />
              </div>

              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900 flex items-start gap-2">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>As permissões aqui são <b>adicionais</b> às do perfil do usuário. Nunca reduzem acessos existentes. Master permanece imune independentemente destes grupos.</span>
              </div>

              {/* Matriz do grupo (compacta) */}
              <div className="rounded-lg border border-slate-200 max-h-[46vh] overflow-y-auto">
                <div className="hidden sm:grid grid-cols-[minmax(180px,1fr)_repeat(6,72px)_88px] items-center px-3 py-1.5 bg-slate-50 text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold border-b sticky top-0 z-10">
                  <span>Módulo</span>
                  {catalog.actions.map((a) => <span key={a.key} className="text-center">{a.label}</span>)}
                  <span className="text-right">Linha</span>
                </div>
                {catalog.modules.map((mod) => {
                  const row = editing.permissions?.[mod.key] || {};
                  return (
                    <div key={mod.key} className="grid grid-cols-[minmax(180px,1fr)_repeat(6,72px)_88px] items-center gap-1 px-3 py-1.5 border-b border-slate-100 text-[12.5px] hover:bg-slate-50/40">
                      <div className="truncate" title={mod.key}>{mod.label}</div>
                      {catalog.actions.map((a) => {
                        const on = !!row[a.key];
                        return (
                          <div key={a.key} className="text-center">
                            <button
                              type="button"
                              disabled={!canManage}
                              onClick={() => toggleCell(mod.key, a.key)}
                              data-testid={`groups-cell-${mod.key}-${a.key}`}
                              className={`h-5 w-9 rounded-full border transition-colors ${on ? "bg-emerald-500 border-emerald-600" : "bg-slate-200 border-slate-300"} ${!canManage ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                            >
                              <span className={`block h-3 w-3 bg-white rounded-full shadow transform transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
                            </button>
                          </div>
                        );
                      })}
                      <div className="text-right flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" disabled={!canManage} onClick={() => bulkRow(mod.key, true)}>Todos</Button>
                        <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" disabled={!canManage} onClick={() => bulkRow(mod.key, false)}>Nenhum</Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Usuários vinculados */}
              {editing.id && detail?.users && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Usuários vinculados</div>
                    {canManage && (
                      <Button variant="outline" size="sm" onClick={openAssign} data-testid="groups-assign-btn">
                        Gerenciar vínculos
                      </Button>
                    )}
                  </div>
                  {detail.users.length === 0 ? (
                    <div className="text-[12px] text-muted-foreground italic">Nenhum usuário vinculado.</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {detail.users.map((u) => (
                        <Badge key={u.id} variant="outline" className="bg-slate-50 text-[11.5px]" data-testid={`groups-user-${u.id}`}>
                          {u.name || u.email} <span className="ml-1 text-muted-foreground">· {u.role}</span>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {canManage && (
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  {editing.id ? (
                    <Button variant="outline" onClick={() => setConfirmDelete(editing)} className="text-red-700 hover:text-red-800" data-testid="groups-delete-btn">
                      <Trash2 className="h-4 w-4 mr-1" /> Excluir grupo
                    </Button>
                  ) : <span />}
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => { setEditing(null); setDetail(null); }}>Fechar</Button>
                    <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} disabled={saving} data-testid="groups-save-btn">
                      <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : editing.id ? "Salvar alterações" : "Criar grupo"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialog de vinculação de usuários */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-2xl" data-testid="groups-assign-dialog">
          <DialogHeader>
            <DialogTitle>Vincular usuários · {editing?.name}</DialogTitle>
            <DialogDescription>Selecione os usuários que devem herdar as permissões deste grupo.</DialogDescription>
          </DialogHeader>
          <div className="mb-2 relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Buscar por nome, e-mail ou perfil..." value={userQuery} onChange={(e) => setUserQuery(e.target.value)} className="pl-8" />
          </div>
          <div className="max-h-[50vh] overflow-y-auto border border-slate-200 rounded-md">
            {usersFiltered.map((u) => {
              const on = assigned.has(u.id);
              return (
                <label key={u.id} className={`flex items-center gap-3 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50/60 ${on ? "bg-emerald-50/40" : ""}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleUser(u.id)} data-testid={`groups-assign-user-${u.id}`} />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium">{u.name}</div>
                    <div className="text-[11px] text-muted-foreground">{u.email} · {u.role}</div>
                  </div>
                  {!u.active && <Badge variant="outline" className="bg-slate-100 text-muted-foreground">inativo</Badge>}
                </label>
              );
            })}
            {usersFiltered.length === 0 && (
              <div className="p-4 text-center text-[12.5px] text-muted-foreground">Nenhum usuário encontrado.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancelar</Button>
            <Button onClick={saveAssign} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="groups-assign-save-btn">
              Salvar vínculos ({assigned.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent data-testid="groups-delete-dialog">
          <DialogHeader>
            <DialogTitle>Excluir grupo &quot;{confirmDelete?.name}&quot;?</DialogTitle>
            <DialogDescription>
              Todos os usuários serão desvinculados automaticamente. Ação registrada em auditoria.
              O grupo será marcado como inativo (soft delete).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="groups-delete-confirm-btn">Sim, excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
